import type { ChangeEvent } from 'react';

import chevronDownUrl from '@/assets/icon-chevron-down.svg';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps {
  readonly id: string;
  /** Visually hidden when the group above already carries a visible label — see `DateOfBirthField`. */
  readonly label: string;
  readonly labelHidden?: boolean;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly options: readonly SelectOption[];
  /** Draws the frame's red asterisk and sets `required`. See `Field` for why both. */
  readonly required?: boolean;
  /**
   * The validation message, shown once the candidate has tried to continue — same contract as `Field`.
   *
   * ⚠️ **This is not decoration, and its absence was a bug.** `useScrollToFirstError` finds the first
   * offending control by querying `[aria-invalid="true"]`, so a select that drew no error state was
   * INVISIBLE to it: a candidate who left State on its placeholder pressed Continue and nothing happened
   * at all — no message, no scroll, no focus. The button read as broken, which is the exact failure the
   * inline errors exist to remove.
   */
  readonly error?: string;
}

/**
 * A dropdown [Figma · Select, 12074:23824].
 *
 * A native `<select>`, not a custom listbox. The frame draws a chevron affordance, and a custom control
 * would have to reimplement keyboard handling, typeahead and the platform picker — on an iPad the native
 * picker is a full-height wheel that is far easier to hit than any div-based menu, which is the whole point
 * on a device operated by a finger.
 *
 * The chevron is drawn with `appearance-none` plus the exported asset as a background, so the affordance
 * matches the design while the control stays native.
 *
 * ⚠️ The asset is imported and applied through `style`, NOT as a Tailwind arbitrary
 * `bg-[url('@/assets/…')]`. Vite does not rewrite the `@/` alias inside a Tailwind class, so that form
 * emits a literal `/@/assets/…` URL, 404s, and the chevron silently never renders — caught in review, not
 * by any test, because a missing background image breaks nothing measurable.
 */
export function Select({
  id,
  label,
  labelHidden = false,
  value,
  onChange,
  placeholder,
  options,
  required,
  error,
}: SelectProps) {
  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className="flex flex-1 flex-col gap-1">
      {/*
        The label is wrapped ONLY when something stands beside it.

        `sr-only` is `position: absolute`, so a hidden label is out of flow and costs no gap — but a
        wrapper around it is in flow, has zero height, and still consumes one `gap-1`. That put every
        `labelHidden` select 4px lower than the frame it was measured against, which is both date-of-birth
        dropdowns on the check-in screen.
      */}
      {labelHidden ? (
        <label className="sr-only" htmlFor={id}>
          {label}
        </label>
      ) : (
        // Sibling, not child: the label's text content IS the control's accessible name [Field.tsx].
        <div className="flex items-center gap-1">
          <label
            /*
              `leading-5` to match `Field`. Without it the label took Tailwind's default 24px line box
              where the input's label uses 20, so a select stood 4px taller than the text field beside it
              — visible on the State/ZIP row, where the frame draws both 64.
            */
            className="text-base font-semibold leading-5 text-[var(--foreground)]"
            htmlFor={id}
          >
            {label}
          </label>
          {required === true && (
            <span aria-hidden="true" className="text-base leading-5 text-[var(--destructive)]">
              *
            </span>
          )}
        </div>
      )}
      <select
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        aria-invalid={error === undefined ? undefined : true}
        className={`kiosk-focusable h-10 w-full appearance-none rounded-[var(--radius-xs)] bg-[var(--card)] p-2 pr-10 text-base leading-6 text-[var(--foreground)] ring-1 ring-inset ${
          error === undefined ? 'ring-[var(--border-subtle)]' : 'ring-[var(--error-border)]'
        }`}
        id={id}
        onChange={handleChange}
        required={required}
        style={{
          backgroundImage: `url("${chevronDownUrl}")`,
          backgroundRepeat: 'no-repeat',
          /*
           * The glyph is 12 x 6, not 24 x 24. The frame nests them: a 24px icon BOX sits 8px from the
           * control's right edge, and the chevron inside it is inset 25% horizontally and 37.5% vertically
           * — so the drawn mark is half the box's width and a quarter of its height. Sizing to the box
           * rendered the chevron at twice its designed size, which is what review caught.
           *
           * 14px from the right = the 8px box offset plus the 6px of empty box beside the glyph.
           */
          backgroundPosition: 'right 14px center',
          backgroundSize: '12px 6px',
        }}
        value={value}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error !== undefined && (
        <span className="text-sm leading-5 text-[var(--error)]" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
